import { useMemo } from 'react';

import { Control, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SingleSelect } from '@/modules/common/components/molecules/selectors/single-select/single-select';
import { SingleSelectOption } from '@/modules/common/components/molecules/selectors/single-select/single-select-types';

import { MembershipType } from '../types/membership-type';

interface MembershipTypeSelectProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Membership Type single-select with hard-coded enum options.
 * No backend fetch — values are agreed upon between frontend and backend.
 */
export function MembershipTypeSelect<T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  disabled = false,
}: MembershipTypeSelectProps<T>) {
  const { t } = useTranslation(['authenticated.team']);

  const options = useMemo((): SingleSelectOption[] => {
    return [
      {
        value: MembershipType.InHouse,
        label: t('membershipTypes.inHouse'),
      },
      {
        value: MembershipType.Partner,
        label: t('membershipTypes.partner'),
      },
      {
        value: MembershipType.Contractor,
        label: t('membershipTypes.contractor'),
      },
    ];
  }, [t]);

  return (
    <SingleSelect
      name={name}
      control={control}
      options={options}
      label={label}
      placeholder={placeholder || t('details.tabs.info.membershipType.placeholder')}
      emptyMessage={t('details.tabs.info.membershipType.noTypesFound')}
      disabled={disabled}
      searchable={false}
      isClearSelectionEnabled={true}
    />
  );
}
